import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiAlertTriangle,
  FiBox,
  FiDollarSign,
  FiPackage,
  FiRepeat,
  FiTrendingDown,
  FiTrendingUp,
  FiXCircle,
} from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";

const number = (value) => Number(value || 0);

const formatNumber = (value) =>
  number(value).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  });

const formatMoney = (value) =>
  `₹${number(value).toLocaleString("en-IN", {
    maximumFractionDigits: 0,
  })}`;

const startOfMonth = (date) =>
  new Date(date.getFullYear(), date.getMonth(), 1);

const sameDayLastMonth = (date) =>
  new Date(date.getFullYear(), date.getMonth() - 1, date.getDate());

const balanceKey = (variantId, storeId) => `${variantId}-${storeId}`;

const getComparison = (current, previous, lowerIsBetter = false) => {
  const difference = current - previous;

  if (previous === 0) {
    if (current === 0) {
      return {
        text: "0%",
        trend: "up",
        progress: 10,
      };
    }

    return {
      text: "New",
      trend: lowerIsBetter ? "down" : "up",
      progress: 70,
    };
  }

  const percentage = Math.abs((difference / previous) * 100);
  const improved = lowerIsBetter ? difference < 0 : difference > 0;

  return {
    text: `${difference > 0 ? "+" : ""}${percentage.toFixed(1)}%`,
    trend: improved ? "up" : "down",
    progress: Math.min(100, Math.max(12, percentage)),
  };
};

export default function InventoryStats() {
  const { accessToken, isLoading: isAuthLoading } = useAuth();

  const [data, setData] = useState({
    balances: [],
    movements: [],
    settings: [],
  });
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(async () => {
    if (!accessToken) return;

    try {
      setLoading(true);

      const [balances, movements, settings] = await Promise.all([
        apiRequest("/inventory/balances", { accessToken }),
        apiRequest("/inventory/movements", { accessToken }),
        apiRequest("/inventory/settings", { accessToken }),
      ]);

      setData({
        balances: balances || [],
        movements: movements || [],
        settings: settings || [],
      });
    } catch {
      setData({
        balances: [],
        movements: [],
        settings: [],
      });
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (isAuthLoading || !accessToken) return;

    loadStats();
  }, [isAuthLoading, accessToken, loadStats]);

  const stats = useMemo(() => {
    const now = new Date();
    const thisMonthStart = startOfMonth(now);
    const todayStart = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );
    const lastMonthSameDayStart = sameDayLastMonth(todayStart);
    const lastMonthSameDayEnd = new Date(
      lastMonthSameDayStart.getFullYear(),
      lastMonthSameDayStart.getMonth(),
      lastMonthSameDayStart.getDate() + 1,
    );

    const currentMonthMovements = data.movements.filter(
      (movement) => new Date(movement.occurredAt) >= thisMonthStart,
    );

    const netChangeByBalance = new Map();

    currentMonthMovements.forEach((movement) => {
      const key = balanceKey(movement.variantId, movement.storeId);

      const current = netChangeByBalance.get(key) || {
        quantityChange: 0,
        valueChange: 0,
      };

      const quantityChange = number(movement.quantityChange);
      const unitCost = number(movement.unitCost);

      netChangeByBalance.set(key, {
        quantityChange: current.quantityChange + quantityChange,
        valueChange: current.valueChange + quantityChange * unitCost,
      });
    });

    const currentProducts = new Set();
    const previousProducts = new Set();

    let currentStockUnits = 0;
    let previousStockUnits = 0;
    let currentInventoryValue = 0;
    let previousInventoryValue = 0;
    let currentOutOfStock = 0;
    let previousOutOfStock = 0;

    const availabilityByBalance = new Map();

    data.balances.forEach((balance) => {
      const key = balanceKey(balance.variantId, balance.storeId);
      const changes = netChangeByBalance.get(key) || {
        quantityChange: 0,
        valueChange: 0,
      };

      const onHand = number(balance.quantityOnHand);
      const reserved = number(balance.quantityReserved);
      const averageUnitCost = number(balance.averageUnitCost);

      const previousOnHand = onHand - changes.quantityChange;
      const previousAvailable = previousOnHand - reserved;

      const productId = balance.variant?.productId;

      if (onHand > 0 && productId) currentProducts.add(productId);
      if (previousOnHand > 0 && productId) previousProducts.add(productId);

      currentStockUnits += onHand;
      previousStockUnits += previousOnHand;

      currentInventoryValue += onHand * averageUnitCost;
      previousInventoryValue +=
        onHand * averageUnitCost - changes.valueChange;

      if (onHand - reserved <= 0) currentOutOfStock += 1;
      if (previousAvailable <= 0) previousOutOfStock += 1;

      availabilityByBalance.set(key, {
        currentAvailable: onHand - reserved,
        previousAvailable,
      });
    });

    let currentLowStock = 0;
    let previousLowStock = 0;

    data.settings.forEach((setting) => {
      if (setting.reorderLevel === null || setting.reorderLevel === undefined) {
        return;
      }

      const availability = availabilityByBalance.get(
        balanceKey(setting.variantId, setting.storeId),
      ) || {
        currentAvailable: 0,
        previousAvailable: 0,
      };

      const reorderLevel = number(setting.reorderLevel);

      if (availability.currentAvailable <= reorderLevel) {
        currentLowStock += 1;
      }

      if (availability.previousAvailable <= reorderLevel) {
        previousLowStock += 1;
      }
    });

    const movementsToday = data.movements.filter((movement) => {
      const date = new Date(movement.occurredAt);
      return date >= todayStart;
    }).length;

    const movementsSameDayLastMonth = data.movements.filter((movement) => {
      const date = new Date(movement.occurredAt);

      return (
        date >= lastMonthSameDayStart && date < lastMonthSameDayEnd
      );
    }).length;

    return [
      {
        title: "Products in Stock",
        value: currentProducts.size,
        comparison: getComparison(
          currentProducts.size,
          previousProducts.size,
        ),
        subtitle: `${currentProducts.size} products with stock on hand`,
        icon: FiPackage,
        iconBg: "bg-blue-100",
        iconColor: "text-blue-600",
      },
      {
        title: "Stock Units",
        value: formatNumber(currentStockUnits),
        comparison: getComparison(currentStockUnits, previousStockUnits),
        subtitle: "Across your accessible locations",
        icon: FiBox,
        iconBg: "bg-green-100",
        iconColor: "text-green-600",
      },
      {
        title: "Inventory Value",
        value: formatMoney(currentInventoryValue),
        comparison: getComparison(
          currentInventoryValue,
          previousInventoryValue,
        ),
        subtitle: "Using weighted average unit cost",
        icon: FiDollarSign,
        iconBg: "bg-purple-100",
        iconColor: "text-purple-600",
      },
      {
        title: "Low Stock",
        value: currentLowStock,
        comparison: getComparison(
          currentLowStock,
          previousLowStock,
          true,
        ),
        subtitle: "At or below reorder level",
        icon: FiAlertTriangle,
        iconBg: "bg-orange-100",
        iconColor: "text-orange-600",
      },
      {
        title: "Out Of Stock",
        value: currentOutOfStock,
        comparison: getComparison(
          currentOutOfStock,
          previousOutOfStock,
          true,
        ),
        subtitle: "No available stock remaining",
        icon: FiXCircle,
        iconBg: "bg-red-100",
        iconColor: "text-red-600",
      },
      {
        title: "Movements Today",
        value: movementsToday,
        comparison: getComparison(
          movementsToday,
          movementsSameDayLastMonth,
        ),
        subtitle: "Compared with the same day last month",
        icon: FiRepeat,
        iconBg: "bg-indigo-100",
        iconColor: "text-indigo-600",
      },
    ];
  }, [data]);

  return (
    <div className="mb-6 grid gap-5 md:grid-cols-3 xl:grid-cols-3">
      {stats.map((item) => {
        const Icon = item.icon;
        const isPositive = item.comparison.trend === "up";

        return (
          <div
            key={item.title}
            className="relative overflow-hidden rounded-3xl border border-gray-100 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
          >
            <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-slate-50" />

            <div className="relative z-10">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    {item.title}
                  </p>

                  <h3 className="mt-3 text-3xl font-bold text-gray-900">
                    {loading ? "—" : item.value}
                  </h3>
                </div>

                <div
                  className={`flex h-14 w-14 items-center justify-center rounded-2xl ${item.iconBg} ${item.iconColor}`}
                >
                  <Icon size={24} />
                </div>
              </div>

              <p className="mt-2 text-xs text-gray-500">
                {item.subtitle}
              </p>

              <div className="mt-5 h-1.5 overflow-hidden rounded-full bg-slate-200/60">
                <div
                  className={`h-full rounded-full ${
                    isPositive ? "bg-green-500" : "bg-orange-500"
                  }`}
                  style={{ width: `${item.comparison.progress}%` }}
                />
              </div>

              <div className="mt-4 flex items-center gap-2">
                <div
                  className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
                    isPositive
                      ? "bg-green-100 text-green-700"
                      : "bg-orange-100 text-orange-700"
                  }`}
                >
                  {isPositive ? (
                    <FiTrendingUp size={12} />
                  ) : (
                    <FiTrendingDown size={12} />
                  )}

                  {item.comparison.text}
                </div>

                <span className="text-xs text-gray-400">
                  vs last month
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}